// Theme persistence + application.
//
// The theme is an attribute on <html> (`data-theme="dark" | "light"`) that
// styles/_console.scss flips its custom properties on. Setting the attribute
// is the whole switch — no reload, no second stylesheet.
//
// Applied from index.js BEFORE React mounts so the first paint is already
// the right theme; doing it in a component gives a flash of the wrong one.

const KEY = "console-theme";

/** "dark" | "light" — stored choice, else the OS preference, else dark. */
export function getTheme() {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === "dark" || saved === "light") return saved;
  } catch {
    // Private mode / blocked storage — fall through to the OS preference.
  }
  try {
    if (window.matchMedia?.("(prefers-color-scheme: light)").matches) return "light";
  } catch { /* no matchMedia */ }
  return "dark";
}

export function applyTheme(theme) {
  const t = theme === "light" ? "light" : "dark";
  document.documentElement.setAttribute("data-theme", t);
  // Bootstrap 5.3 reads this and recolours every one of its components.
  // Without it, `variant="primary"` stays Bootstrap blue and `.text-muted`
  // stays a light-mode grey — unreadable on a dark surface.
  document.documentElement.setAttribute("data-bs-theme", t);
  // Keeps the mobile browser chrome in step with the app.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", t === "light" ? "#f6f8fb" : "#0a0d14");
  return t;
}

export function setTheme(theme) {
  const t = applyTheme(theme);
  try { localStorage.setItem(KEY, t); } catch { /* not fatal — just not remembered */ }
  return t;
}

/** Call once at startup, before React renders. */
export function initTheme() {
  return applyTheme(getTheme());
}
